'use client'

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetUnitLoadPositionConsolidatedDataQuery } from '@/redux/slices/preship/preship-api-slice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";

interface ConsolidatedViewProps {
  isOpen: boolean;
  onClose: () => void;
  variant: string;
}

const UnitLoadPositionConsolidatedView: React.FC<ConsolidatedViewProps> = ({ isOpen, onClose, variant }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('max');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetUnitLoadPositionConsolidatedDataQuery({ projectId, variant }, { refetchOnMountOrArgChange: true });
  console.log(consolidatedData,'consolidatedData')
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div></div>;

  const renderRawDataTable = () => {
    // 1. Group raw data by palletId
    const groupedByPalletId = consolidatedData.rawData.reduce((groups: Record<string, any[]>, row: any) => {
      const palletId = row.palletId || 'unknown';
      if (!groups[palletId]) {
        groups[palletId] = [];
      }
      groups[palletId].push(row);
      return groups;
    }, {});

    // 2. Sort groups by palletCreationDate and then sort each group by lpnIndex
    const sortedGroups = Object.entries(groupedByPalletId)
      // Sort groups by palletCreationDate
      .sort(([, groupA], [, groupB]) => {
        const dateA = new Date(groupA[0]?.palletCreationDate || 0).getTime();
        const dateB = new Date(groupB[0]?.palletCreationDate || 0).getTime();
        return dateA - dateB;
      })
      .map(([, group]) => {
        // Sort each group by lpnIndex
        return group.sort((a, b) => {
          const indexA = parseInt(a.lpnIndex) || 0;
          const indexB = parseInt(b.lpnIndex) || 0;
          return indexA - indexB;
        });
      });

    // 3. Flatten the sorted groups back into a single array
    const sortedData = sortedGroups.flat();

    return (
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet No</TableHead>
            <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pallet Name</TableHead>
            <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pallet Type</TableHead>
            {variant === 'postShip' ? (
              <>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side A</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side B</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side C</TableHead>
                <TableHead colSpan={3} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side D</TableHead>
              </>
            ) : (
              <>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side A Position (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side B Position (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side C Position (in)</TableHead>
                <TableHead rowSpan={2} className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side D Position (in)</TableHead>
              </>
            )}
          </TableRow>
          {variant === 'postShip' && (
            <TableRow>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Post-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Post-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Post-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pre-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Post-Ship</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Calculated</TableHead>
            </TableRow>
          )}
        </TableHeader>
        <TableBody>
          {sortedData.map((row: any, index: number) => (
            <TableRow key={`${row.palletId}-${row.lpnIndex || index}`} className={"bg-gray-100"}>
              <TableCell className="border text-center border-gray-300 text-center p-2">{row.lpnIndex || index + 1}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
              {variant === 'postShip' ? (
                <>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideAPreShip}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideAPostShip}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row.sideAStatus === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row.sideACalculated}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideBPreShip}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideBPostShip}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row.sideBStatus === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row.sideBCalculated}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideCPreShip}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideCPostShip}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row.sideCStatus === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row.sideCCalculated}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideDPreShip}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideDPostShip}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", row.sideDStatus === 'Pass' ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row.sideDCalculated}</TableCell>
                </>
              ) : (
                <>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideA}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideB}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideC}</TableCell>
                  <TableCell className="border border-gray-300 text-center p-2">{row.sideD}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderStatisticsTable = () => (
    <div className="space-y-6">
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Statistic</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side A Position (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side B Position (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side C Position (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side D Position (in)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {['Max', 'Min', 'Average', 'stdev', 'Count'].map((stat, index) => (
            <React.Fragment key={stat}>
              {consolidatedData.statistics[stat.toLowerCase()].map((row: any, rowIndex: number) => (
                <TableRow key={`${stat}-${rowIndex}`} className={"bg-gray-100"}>
                  {rowIndex === 0 && (
                    <TableCell rowSpan={consolidatedData.statistics[stat.toLowerCase()].length} className="border border-gray-300 text-center p-2 font-semibold">
                      {stat}
                    </TableCell>
                  )}
                  <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", variant === 'postShip' && stat !== 'Count' && parseFloat(row.sideA) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row.sideA}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", variant === 'postShip' && stat !== 'Count' && parseFloat(row.sideB) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row.sideB}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", variant === 'postShip' && stat !== 'Count' && parseFloat(row.sideC) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row.sideC}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", variant === 'postShip' && stat !== 'Count' && parseFloat(row.sideD) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row.sideD}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Data Fill Status</TableHead>
            {variant === 'postShip' && <>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Overall pass/fail status</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side A Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side A Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side B Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side B Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side C Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side C Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side D Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 font-semibold">Side D Pass Count</TableHead>
            </>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
              {variant === 'postShip' && <>
                <TableCell className={`${row.overallStatus === 'Fail' ? "text-white bg-bgRed" : row.overallStatus === 'Pass' ? " text-white bg-states-success" : ""} border border-gray-300 text-center p-2`}>{row.overallStatus}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideAFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideAFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideAPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideAPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideBFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideBFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideBPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideBPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideCFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideCFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideCPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideCPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideDFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideDFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideDPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideDPassCount}</TableCell>
              </>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletType,
      SideA: parseFloat(item.sideA),
      SideB: parseFloat(item.sideB),
      SideC: parseFloat(item.sideC),
      SideD: parseFloat(item.sideD),
    }));

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['max', 'min', 'average', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start hover:bg-blue hover:text-white",
                activeStatistic === stat ? " bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="SideA" fill="#004899" />
            <Bar barSize={20} dataKey="SideB" fill="#82ca9d" />
            <Bar barSize={20} dataKey="SideC" fill="#ffc658" />
            <Bar barSize={20} dataKey="SideD" fill="#ff7300" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Unit Load Position Consolidated View {`(${variant === 'postShip' ? "Post Ship" : variant === 'preShip' ? "Pre Ship" : "Post Stack"})`}</DialogTitle>
        </DialogHeader>
        <div className="flex h-[calc(100vh-200px)]">
          <div className="w-48 pt-10 bg-bgBlue">
            <div className="">
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'raw' ? " bg-blue text-white" : " text-gray-700 bg-bgBlue"
                )}
                onClick={() => setActiveTab('raw')}
              >
                Raw Data
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'statistics' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('statistics')}
              >
                Statistics
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'graphs' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('graphs')}
              >
                Graphs
              </Button>
            </div>
          </div>
          <div className="flex-1 p-6 overflow-auto">
            {activeTab === 'raw' && renderRawDataTable()}
            {activeTab === 'statistics' && renderStatisticsTable()}
            {activeTab === 'graphs' && renderGraph()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnitLoadPositionConsolidatedView;
